import { BASE_URL } from '@/utils/api';

// Partner portal API client. Deliberately its own localStorage keys, separate
// from the employee session and the client portal session, so a partner login
// can never be picked up by another part of the app (and vice versa).

const TOKEN_KEY = 'partnerPortalToken';
const PARTNER_ID_KEY = 'partnerPortalPartnerId';
const PARTNER_NAME_KEY = 'partnerPortalPartnerName';

export interface PartnerLoginResult {
  token: string;
  partnerId: number;
  partnerCode: string;
  partnerName: string;
  username: string;
}

export interface PartnerMe {
  partnerId: number;
  partnerCode: string;
  partnerName: string;
  mobile: string;
  email: string | null;
}

// What a case is expected to earn, from the rate saved when it was opened.
// status: FIXED, CALCULATED, NO_RATE, PENDING_FEE, FEE_NOT_SPLIT, BASE_ZERO, NOT_SAVED.
// message is safe for the partner; rateLabel (the rate itself) is filled for staff only.
export interface ExpectedCommission {
  amount: number;
  status: string;
  message: string | null;
  rateLabel: string | null;
}

// A payment the partner recorded for a client: amount, date and where it stands. The UPI reference
// and proof stay with Beyonli's staff.
export interface PartnerRecordedPayment {
  paymentId: number;
  amount: number;
  paymentDate: string;
  status: 'Pending' | 'Confirmed' | 'Rejected';
  rejectionReason: string | null;
}

// The money on one case, as the partner sees it (read-only totals, their own fee proposal and the
// payments they recorded).
export interface PartnerCaseFee {
  totalFee: number | null; // null until a fee is live
  paid: number; // confirmed by Beyonli
  pending: number; // recorded, waiting for Beyonli to confirm
  balance: number | null;
  proposalStatus: 'PENDING' | 'APPROVED' | 'REJECTED' | 'WITHDRAWN' | 'SUPERSEDED' | null;
  proposedTotal: number | null;
  approvedTotal: number | null;
  rejectReason: string | null;
  pendingProposalId: number | null;
  canPropose: boolean;
  proposeBlockedReason: string | null;
  canRecordPayment: boolean;
  recordBlockedReason: string | null;
  payments: PartnerRecordedPayment[];
}

// Beyonli's UPI details - shown to the client so they can pay in front of the partner.
export interface PaymentQr {
  qrImagePath: string | null;
  upiId: string | null;
  upiMobileNumber: string | null;
}

export interface PartnerCase {
  caseId: number;
  caseCode: string;
  serviceName: string | null;
  financialYear: string;
  currentStageNum: number;
  status: string;
  dueDate: string | null;
  updatedAt: string;
  commissionEarned: number;
  expectedCommission?: ExpectedCommission | null;
  fee?: PartnerCaseFee | null;
}

export interface PartnerClient {
  clientId: number;
  clientCode: string;
  clientType: 'BUSINESS' | 'INDIVIDUAL';
  fullName: string;
  businessName: string | null;
  mobile: string;
  whatsappNumber: string | null;
  email: string | null;
  cases: PartnerCase[];
  commissionEarned: number;
}

export interface PartnerPortalDocument {
  documentId: number;
  caseId: number;
  caseCode: string | null;
  clientId: number | null;
  clientName: string | null;
  name: string;
  category: string;
  isRequired: boolean | null;
  status: string;
  fileName: string | null;
  filePath: string | null;
  uploadedByType: 'CLIENT' | 'EMPLOYEE' | 'PARTNER' | null;
  rejectionReason: string | null;
  requestedAt: string;
  uploadedAt: string | null;
}

export type ReferralStatus = 'PENDING' | 'ACCEPTED' | 'REJECTED';

export interface Referral {
  referralId: number;
  partnerId: number;
  partnerCode: string | null;
  partnerName: string | null;
  clientType: 'BUSINESS' | 'INDIVIDUAL';
  fullName: string;
  businessName: string | null;
  pan: string;
  mobile: string;
  email: string | null;
  categoryId: number | null;
  categoryName: string | null;
  servicesRequested: string | null;
  notes: string | null;
  status: ReferralStatus;
  submittedAt: string;
  reviewedByName: string | null;
  reviewedAt: string | null;
  rejectReason: string | null;
  clientId: number | null;
  clientCode: string | null;
}

export interface ReferralPayload {
  clientType: 'BUSINESS' | 'INDIVIDUAL';
  fullName: string;
  businessName?: string;
  pan: string;
  mobile: string;
  email?: string;
  categoryId?: number;
  servicesRequested?: string;
  notes?: string;
}

export interface ClientCategoryOption {
  categoryId: number;
  categoryName: string;
  colorClass: string;
}

export type CommissionStatus = 'ACTIVE' | 'CANCELLED';

export interface Commission {
  commissionId: number;
  partnerId: number;
  partnerName: string | null;
  caseId: number;
  caseCode: string | null;
  clientId: number | null;
  clientName: string | null;
  serviceName: string | null;
  financialYear: string | null;
  amount: number;
  commissionDate: string;
  note: string | null;
  status: CommissionStatus;
  cancelReason: string | null;
  createdByName: string | null;
  createdAt: string;
}

export type PayoutStatus = 'PENDING_CONFIRMATION' | 'CONFIRMED' | 'CANCELLED';

// The part of a payout that paid one case.
export interface PayoutAllocation {
  caseId: number;
  caseCode: string | null;
  clientName: string | null;
  serviceName: string | null;
  financialYear: string | null;
  amount: number;
}

// One case's commission position.
export interface CaseLedgerRow {
  caseId: number;
  caseCode: string;
  clientId: number;
  clientName: string;
  serviceName: string | null;
  financialYear: string;
  earned: number;
  paid: number; // payouts the partner confirmed
  awaitingConfirmation: number;
  // What can still be paid on this case (earned, plus the expected commission while the case is
  // open, minus paid and awaiting). Negative = paid more than the case earned (to be recovered).
  due: number;
  caseStatus?: string; // ACTIVE, ON_HOLD, COMPLETED, CANCELLED
  // Still to be earned when an open case completes.
  expectedOnCompletion?: number;
  // Paid or awaiting confirmation beyond what the case has earned so far.
  advance?: number;
}

export interface Payout {
  payoutId: number;
  payoutNo: string;
  partnerId: number;
  partnerName: string | null;
  amount: number;
  paidOn: string;
  paymentMode: string;
  referenceNo: string | null;
  note: string | null;
  proofFilePath: string | null;
  status: PayoutStatus;
  recordedByName: string | null;
  recordedAt: string;
  confirmedAt: string | null;
  cancelledAt: string | null;
  cancelReason: string | null;
  allocations: PayoutAllocation[];
}

export interface LedgerSummary {
  earned: number;
  paid: number;
  confirmed: number;
  awaitingConfirmation: number;
  balance: number;
}

export interface Earnings {
  ledger: LedgerSummary;
  commissions: Commission[];
  payouts: Payout[];
}

export interface PartnerNotification {
  notificationId: number;
  notificationType: string;
  title: string;
  message: string;
  referenceId: number | null;
  linkUrl: string | null;
  isRead: boolean;
  createdAt: string;
}

export interface PagedResult<T> {
  content: T[];
  currentPage: number;
  totalItems: number;
  totalPages: number;
}

function authHeader(): Record<string, string> {
  const token = typeof window !== 'undefined' ? localStorage.getItem(TOKEN_KEY) || '' : '';
  return { Authorization: `Bearer ${token}` };
}

function jsonHeaders(): Record<string, string> {
  return { ...authHeader(), 'Content-Type': 'application/json' };
}

async function handle<T>(res: Response, fallback: string): Promise<T> {
  const result = await res.json().catch(() => ({}));
  if (!res.ok || result?.status === false) {
    throw new Error(result?.message || fallback);
  }
  return result.data as T;
}

async function handleWithMessage<T>(
  res: Response,
  fallback: string
): Promise<{ data: T; message: string }> {
  const result = await res.json().catch(() => ({}));
  if (!res.ok || result?.status === false) {
    throw new Error(result?.message || fallback);
  }
  return { data: result.data as T, message: result.message || '' };
}

export function getPartnerPortalToken(): string | null {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem(TOKEN_KEY);
}

export function storePartnerPortalSession(result: PartnerLoginResult) {
  localStorage.setItem(TOKEN_KEY, result.token);
  localStorage.setItem(PARTNER_ID_KEY, String(result.partnerId));
  localStorage.setItem(PARTNER_NAME_KEY, result.partnerName);
}

export function clearPartnerPortalSession() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(PARTNER_ID_KEY);
  localStorage.removeItem(PARTNER_NAME_KEY);
}

export async function fetchPartnerMe(): Promise<PartnerMe> {
  const res = await fetch(`${BASE_URL}/api/partner-portal/me`, { headers: authHeader() });
  return handle(res, 'Failed to load your profile');
}

export async function fetchMyClients(
  search: string,
  page: number,
  size: number
): Promise<PagedResult<PartnerClient>> {
  const q = new URLSearchParams({ search, page: String(page), size: String(size) });
  const res = await fetch(`${BASE_URL}/api/partner-portal/clients?${q}`, { headers: authHeader() });
  return handle(res, 'Failed to load your clients');
}

export async function fetchPartnerDocuments(): Promise<PartnerPortalDocument[]> {
  const res = await fetch(`${BASE_URL}/api/partner-portal/documents`, { headers: authHeader() });
  return handle(res, 'Failed to load documents');
}

export async function uploadPartnerDocument(documentId: number, file: File): Promise<string> {
  const form = new FormData();
  form.append('file', file);
  const res = await fetch(`${BASE_URL}/api/partner-portal/documents/${documentId}/upload`, {
    method: 'POST',
    headers: authHeader(),
    body: form,
  });
  const result = await handleWithMessage<null>(res, 'Failed to upload the document');
  return result.message;
}

export async function fetchMyReferrals(): Promise<Referral[]> {
  const res = await fetch(`${BASE_URL}/api/partner-portal/referrals`, { headers: authHeader() });
  return handle(res, 'Failed to load your referrals');
}

export interface ServiceRequest {
  requestId: number;
  clientId: number;
  clientCode: string | null;
  clientName: string | null;
  servicesRequested: string;
  notes: string | null;
  status: ReferralStatus;
  submittedAt: string;
  reviewedByName: string | null;
  reviewedAt: string | null;
  rejectReason: string | null;
  createdCaseCodes: string | null;
}

export async function fetchMyServiceRequests(): Promise<ServiceRequest[]> {
  const res = await fetch(`${BASE_URL}/api/partner-portal/service-requests`, {
    headers: authHeader(),
  });
  return handle(res, 'Failed to load your service requests');
}

export async function submitServiceRequest(payload: {
  clientId: number;
  servicesRequested: string;
  notes?: string;
}): Promise<string> {
  const res = await fetch(`${BASE_URL}/api/partner-portal/service-requests`, {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify(payload),
  });
  const result = await handleWithMessage<ServiceRequest>(res, 'Failed to send the request');
  return result.message;
}

export async function fetchClientCategoryOptions(
  clientType: 'BUSINESS' | 'INDIVIDUAL'
): Promise<ClientCategoryOption[]> {
  const res = await fetch(
    `${BASE_URL}/api/partner-portal/client-categories?clientType=${clientType}`,
    { headers: authHeader() }
  );
  return handle(res, 'Failed to load the client categories');
}

export async function fetchServiceNames(): Promise<string[]> {
  const res = await fetch(`${BASE_URL}/api/partner-portal/services`, { headers: authHeader() });
  return handle(res, 'Failed to load the services');
}

export async function submitReferral(payload: ReferralPayload): Promise<string> {
  const res = await fetch(`${BASE_URL}/api/partner-portal/referrals`, {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify(payload),
  });
  const result = await handleWithMessage<Referral>(res, 'Failed to submit the client');
  return result.message;
}

// ---- fee and client payments ----

// The partner proposes ONE total for a client's case; Beyonli approves it before the client can pay.
export async function proposeFee(caseId: number, totalFee: number): Promise<string> {
  const res = await fetch(`${BASE_URL}/api/partner-portal/cases/${caseId}/fee-proposal`, {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify({ totalFee }),
  });
  const result = await handleWithMessage<unknown>(res, 'Failed to send the fee');
  return result.message;
}

export async function withdrawFeeProposal(proposalId: number): Promise<string> {
  const res = await fetch(`${BASE_URL}/api/partner-portal/fee-proposals/${proposalId}`, {
    method: 'DELETE',
    headers: authHeader(),
  });
  const result = await handleWithMessage<null>(res, 'Failed to withdraw the fee');
  return result.message;
}

export async function fetchPaymentQr(): Promise<PaymentQr> {
  const res = await fetch(`${BASE_URL}/api/partner-portal/payment-qr`, { headers: authHeader() });
  return handle(res, 'Failed to load the payment QR');
}

export interface RecordClientPaymentPayload {
  amount: number;
  referenceNumber: string;
  paymentDate?: string; // yyyy-mm-dd, defaults to today
  remarks?: string;
}

// The partner records a payment the client made to Beyonli's QR. It counts once Beyonli confirms it.
export async function recordClientPayment(
  caseId: number,
  payload: RecordClientPaymentPayload,
  proof?: File | null
): Promise<string> {
  const form = new FormData();
  form.append('payload', new Blob([JSON.stringify(payload)], { type: 'application/json' }));
  if (proof) form.append('proof', proof);
  const res = await fetch(`${BASE_URL}/api/partner-portal/cases/${caseId}/payments`, {
    method: 'POST',
    headers: authHeader(),
    body: form,
  });
  const result = await handleWithMessage<null>(res, 'Failed to record the payment');
  return result.message;
}

export async function fetchMyCaseLedger(): Promise<CaseLedgerRow[]> {
  const res = await fetch(`/api/partner-portal/case-ledger`, { headers: authHeader() });
  return handle(res, 'Failed to load your case-wise earnings');
}

export async function fetchEarnings(): Promise<Earnings> {
  const res = await fetch(`${BASE_URL}/api/partner-portal/earnings`, { headers: authHeader() });
  return handle(res, 'Failed to load your earnings');
}

export async function confirmPayout(payoutId: number): Promise<string> {
  const res = await fetch(`${BASE_URL}/api/partner-portal/payouts/${payoutId}/confirm`, {
    method: 'POST',
    headers: authHeader(),
  });
  const result = await handleWithMessage<Payout>(res, 'Failed to confirm the payout');
  return result.message;
}

// ---- notifications ----

export async function fetchPartnerNotifications(): Promise<PartnerNotification[]> {
  const res = await fetch(`${BASE_URL}/api/partner-portal/notifications`, {
    headers: authHeader(),
  });
  if (!res.ok) throw new Error('Failed to fetch notifications');
  return res.json().catch(() => []);
}

export async function fetchPartnerUnreadCount(): Promise<number> {
  const res = await fetch(`${BASE_URL}/api/partner-portal/notifications/unread-count`, {
    headers: authHeader(),
  });
  const result = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error('Failed to fetch unread count');
  return result.count || 0;
}

export async function markPartnerNotificationRead(id: number): Promise<void> {
  await fetch(`${BASE_URL}/api/partner-portal/notifications/${id}/read`, {
    method: 'PUT',
    headers: authHeader(),
  });
}

export async function markAllPartnerNotificationsRead(): Promise<void> {
  await fetch(`${BASE_URL}/api/partner-portal/notifications/read-all`, {
    method: 'PUT',
    headers: authHeader(),
  });
}
